/**
 * Quotes Store Module
 * Handles quotes data and actions
 */
import quoteService from '@/services/quoteService';

const state = {
  quotes: [],
  currentQuote: null,
  isLoading: false,
  error: null
};

const mutations = {
  SET_QUOTES(state, quotes) {
    state.quotes = quotes;
  },
  
  SET_CURRENT_QUOTE(state, quote) {
    state.currentQuote = quote;
  },
  
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  
  SET_ERROR(state, error) {
    state.error = error;
  },
  
  ADD_QUOTE(state, quote) {
    state.quotes.unshift(quote);
  },
  
  UPDATE_QUOTE(state, updatedQuote) {
    const index = state.quotes.findIndex(quote => quote.id === updatedQuote.id);
    if (index !== -1) {
      state.quotes.splice(index, 1, updatedQuote);
    }
    
    if (state.currentQuote && state.currentQuote.id === updatedQuote.id) {
      state.currentQuote = updatedQuote;
    }
  },
  
  REMOVE_QUOTE(state, quoteId) {
    state.quotes = state.quotes.filter(quote => quote.id !== quoteId);
    
    if (state.currentQuote && state.currentQuote.id === quoteId) {
      state.currentQuote = null;
    }
  }
};

const actions = {
  /**
   * Fetch all quotes for the current user
   */
  async fetchQuotes({ commit, rootGetters }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const customerId = rootGetters['customer/customerInfo'].customerId;
      if (!customerId) {
        throw new Error('Customer ID not found');
      }
      
      const response = await quoteService.getQuotes(customerId);
      commit('SET_QUOTES', response.data);
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch quotes');
      console.error('Error fetching quotes:', error);
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  /**
   * Fetch a specific quote by ID
   */
  async fetchQuote({ commit }, quoteId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const response = await quoteService.getQuote(quoteId);
      commit('SET_CURRENT_QUOTE', response.data);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch quote');
      console.error('Error fetching quote:', error);
      return null;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  /**
   * Submit a new quote
   */
  async submitQuote({ commit }, quoteData) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const response = await quoteService.submitQuote(quoteData);
      commit('ADD_QUOTE', response.data);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to submit quote');
      console.error('Error submitting quote:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  /**
   * Update an existing quote
   */
  async updateQuote({ commit }, { quoteId, quoteData }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const response = await quoteService.updateQuote(quoteId, quoteData);
      commit('UPDATE_QUOTE', response.data);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update quote');
      console.error('Error updating quote:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  /**
   * Delete a quote
   */
  async deleteQuote({ commit }, quoteId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      await quoteService.deleteQuote(quoteId);
      commit('REMOVE_QUOTE', quoteId);
      return true;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete quote');
      console.error('Error deleting quote:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  /**
   * Clear current quote
   */
  clearCurrentQuote({ commit }) {
    commit('SET_CURRENT_QUOTE', null);
  }
};

const getters = {
  /**
   * Get all quotes
   */
  quotes: state => state.quotes,
  
  /**
   * Get current quote
   */
  currentQuote: state => state.currentQuote,
  
  /**
   * Get loading state
   */
  isLoading: state => state.isLoading,
  
  /**
   * Get error
   */
  error: state => state.error,
  
  /**
   * Check if quotes are loaded
   */
  hasQuotes: state => state.quotes.length > 0
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}; 